export type PlanKey = 'free' | 'bronze' | 'silver' | 'custom' | string;

export interface PlanConfig {
  key: PlanKey;
  name: string;
  description: string;
  commissionRate: number; // Percentage, e.g. 2.75
  gstRate: number; // 18.0% GST on the fee
  maxParticipantsPerActivity: number | null; // null = unlimited or custom override
  paidEventsAllowed: boolean;
  allowedActivityTypes: string[];
  features: string[];
  checklist: string[];
  isDefault?: boolean;
  isPopular?: boolean;
  displayOrder: number;
}

export const ALL_ACTIVITY_TYPES = [
  'workshop',
  'bootcamp',
  'seminar',
  'quiz',
  'hackathon',
  'ideathon',
  'mun',
  'gamethon',
  'conference',
  'codeathon',
  'coding_contest',
  'cultural',
  'startup',
  'college_fest',
  'paper_presentation',
  'debate',
  'other',
  'parliamentary',
  'state_legislative',
] as const;

export type ActivityTypeKey = (typeof ALL_ACTIVITY_TYPES)[number];

export const ACTIVITY_TYPE_LABELS: Record<string, string> = {
  workshop: 'Workshop',
  bootcamp: 'Bootcamp',
  seminar: 'Seminar',
  quiz: 'Quiz & Trivia',
  hackathon: 'Hackathon',
  ideathon: 'Ideathon',
  mun: 'Model United Nations (MUN)',
  gamethon: 'Gamethon & Esports',
  conference: 'Conference & Summit',
  codeathon: 'Codeathon',
  coding_contest: 'Coding Contest',
  cultural: 'Cultural Fest & Talent',
  startup: 'Startup Pitch & Expo',
  college_fest: 'College & Tech Fest',
  paper_presentation: 'Paper Presentation',
  debate: 'Debate & Oratory',
  other: 'Other Custom Event',
  parliamentary: 'Parliamentary Debate / Assembly',
  state_legislative: 'State Legislative Assembly',
};

export const ALL_FEATURES = [
  'basic_registration',
  'communication',
  'qr_ticketing',
  'attendance',
  'support',
  'forms',
  'team_registration',
  'analytics',
  'reports',
  'certificates',
  'jury',
] as const;

export type FeatureKey = (typeof ALL_FEATURES)[number];

export const FEATURE_LABELS: Record<FeatureKey, { name: string; description: string }> = {
  basic_registration: {
    name: 'Basic Registration',
    description: 'Event registration form and attendee list.',
  },
  communication: {
    name: 'Communications',
    description: 'Email broadcasts, attendee announcements, and messaging.',
  },
  qr_ticketing: {
    name: 'QR Ticketing',
    description: 'Digital tickets with secure QR codes for entry scanning.',
  },
  attendance: {
    name: 'Attendance & Check-in',
    description: 'Live QR check-in desk and session attendance logs.',
  },
  support: {
    name: 'Attendee Support',
    description: 'Direct support chat and issue ticketing for participants.',
  },
  forms: {
    name: 'Custom Forms',
    description: 'Custom question forms, waivers, and data collection.',
  },
  team_registration: {
    name: 'Team Registration',
    description: 'Multi-member team signups, leader assignment, and rosters.',
  },
  analytics: {
    name: 'Advanced Analytics',
    description: 'Deep funnel traffic, registration conversion, and revenue trends.',
  },
  reports: {
    name: 'Exportable Reports',
    description: 'Detailed CSV/Excel exports of attendees, financials, and logs.',
  },
  certificates: {
    name: 'Digital Certificates',
    description: 'Automated certificate generation and verifiable badges.',
  },
  jury: {
    name: 'Jury & Evaluation',
    description: 'Scoring rubrics, judge panels, and submission evaluations.',
  },
};

export const PLANS_CONFIG: Record<PlanKey, PlanConfig> = {
  free: {
    key: 'free',
    name: 'Free',
    description: 'Get started with basic event management for free events.',
    commissionRate: 0.0,
    gstRate: 18.0,
    maxParticipantsPerActivity: 100,
    paidEventsAllowed: false,
    allowedActivityTypes: ['workshop', 'bootcamp', 'seminar', 'quiz'],
    features: ['basic_registration', 'qr_ticketing'],
    checklist: [
      'Allowed activity formats: Workshops, Bootcamps, Seminars, Quiz (only these 4 — no other activity types available on this plan)',
      'Participants allowed: 100 per event',
      'QR Ticketing',
    ],
    isDefault: true,
    isPopular: false,
    displayOrder: 1,
  },
  bronze: {
    key: 'bronze',
    name: 'Bronze',
    description: 'Ideal for growing events with ticketing, communications, and check-ins.',
    commissionRate: 2.75,
    gstRate: 18.0,
    maxParticipantsPerActivity: 1000,
    paidEventsAllowed: true,
    allowedActivityTypes: ['workshop', 'bootcamp', 'seminar', 'quiz'],
    features: [
      'basic_registration',
      'communication',
      'qr_ticketing',
      'attendance',
      'support',
      'forms',
      'analytics',
    ],
    checklist: [
      'Allowed activity formats: Workshops, Bootcamps, Seminars, Quiz (same 4 as Free, still limited — Bronze does not unlock additional activity types)',
      'Participants allowed: 1,000 per event',
      'Communication (email/communication campaigns)',
      'QR Ticketing',
      'Attendance',
      'Support',
      'Forms',
      'Analytics',
      'All features available, but scoped only to the 4 allowed activity formats above',
    ],
    isDefault: false,
    isPopular: false,
    displayOrder: 2,
  },
  silver: {
    key: 'silver',
    name: 'Silver',
    description: 'Comprehensive tier for scaling organizations. Complete feature access for up to 3,000 participants.',
    commissionRate: 3.75,
    gstRate: 18.0,
    maxParticipantsPerActivity: 3000,
    paidEventsAllowed: true,
    allowedActivityTypes: [
      'workshop',
      'bootcamp',
      'seminar',
      'quiz',
      'hackathon',
      'ideathon',
      'mun',
      'gamethon',
      'conference',
      'codeathon',
      'coding_contest',
      'cultural',
      'startup',
      'college_fest',
      'paper_presentation',
      'debate',
      'other',
      'parliamentary',
      'state_legislative',
    ],
    features: [
      'basic_registration',
      'communication',
      'qr_ticketing',
      'attendance',
      'support',
      'forms',
      'team_registration',
      'analytics',
      'reports',
      'certificates',
      'jury',
    ],
    checklist: [
      'Allowed activity formats: Hackathon, Ideathon, MUN, Gama-thon, PLUS no restriction on activity type beyond these — Silver has "no limit" on activity formats (all available formats on the platform are usable, not just these 4 named ones)',
      'Participants allowed: 3,000 per event',
      'Team-based Registration (participants can register as a team, e.g. for a hackathon)',
      'QR Ticketing',
      'Attendance',
      'Support',
      'Forms',
      'Communication',
      'All features unlocked, no activity-type limit',
      '10 email campaigns per event',
      'Custom/complimentary registrations available on request, subject to EnthiosHub approval',
    ],
    isDefault: false,
    isPopular: true,
    displayOrder: 3,
  },
  custom: {
    key: 'custom',
    name: 'Custom',
    description: 'Tailored limits, custom commission rate, and dedicated support for large-scale operations.',
    commissionRate: 0.0,
    gstRate: 18.0,
    maxParticipantsPerActivity: null,
    paidEventsAllowed: true,
    allowedActivityTypes: [
      'workshop',
      'bootcamp',
      'seminar',
      'quiz',
      'hackathon',
      'ideathon',
      'mun',
      'gamethon',
      'conference',
      'codeathon',
      'coding_contest',
      'cultural',
      'startup',
      'college_fest',
      'paper_presentation',
      'debate',
      'other',
      'parliamentary',
      'state_legislative',
    ],
    features: [
      'basic_registration',
      'communication',
      'qr_ticketing',
      'attendance',
      'support',
      'forms',
      'team_registration',
      'analytics',
      'reports',
      'certificates',
      'jury',
    ],
    checklist: [
      'Fully negotiated per organization: tailored fee rates, high-capacity limits, custom integrations, dedicated SLA',
      'Custom registrations configurable per organization, subject to EnthiosHub approval',
      'Contact Support (dedicated/priority support included in the Custom plan)',
    ],
    isDefault: false,
    isPopular: false,
    displayOrder: 4,
  },
};

export const PLAN_KEYS: PlanKey[] = ['free', 'bronze', 'silver', 'custom'];

export const DOTTED_TO_SHORT_FEATURE_MAP: Record<string, string> = {
  'attendance.management': 'attendance',
  'forms.custom_builder': 'forms',
  'qr.ticket_generation': 'qr_ticketing',
  'ticketing.qr_checkin': 'qr_ticketing',
  'qr_checkin': 'qr_ticketing',
  'participant.team_registration': 'team_registration',
  'team-management': 'team_registration',
  'analytics.basic': 'analytics',
  'analytics.advanced': 'analytics',
  'analytics.export_reports': 'reports',
  'analytics.export': 'reports',
  'certificates.bulk_generation': 'certificates',
  'jury.management': 'jury',
  'jury.panel_scoring': 'jury',
  evaluation: 'jury',
  'communication.external.broadcast': 'communication',
  communications: 'communication',
  'email.campaigns': 'communication',
  'email_campaigns': 'communication',
  'participant.registration': 'basic_registration',
  'registration.basic': 'basic_registration',
  'finance.paid_events': 'paid_events',
};

export function getPlan(key?: string | null): PlanConfig {
  const normalized = (key || 'silver').toLowerCase() as PlanKey;
  return PLANS_CONFIG[normalized] ?? PLANS_CONFIG.silver;
}

export function isFeatureLocked(currentPlanKey: string, feature: string): boolean {
  const normalizedFeature = DOTTED_TO_SHORT_FEATURE_MAP[feature] || feature;
  const plan = getPlan(currentPlanKey);
  return !plan.features.includes(normalizedFeature) && !plan.features.includes(feature);
}

export function isActivityLocked(currentPlanKey: string, activityType: string): boolean {
  const act = activityType.toLowerCase();
  const normalizedAct = act === 'idea_pitching' ? 'ideathon' : act === 'ideathon' ? 'idea_pitching' : act;
  const plan = getPlan(currentPlanKey);
  return !plan.allowedActivityTypes.includes(act) && !plan.allowedActivityTypes.includes(normalizedAct);
}

export function isPaidEventsLocked(currentPlanKey: string): boolean {
  const plan = getPlan(currentPlanKey);
  return !plan.paidEventsAllowed;
}

export function isParticipantLimitExceeded(currentPlanKey: string, count: number): boolean {
  const plan = getPlan(currentPlanKey);
  if (plan.maxParticipantsPerActivity === null) return false;
  return count > plan.maxParticipantsPerActivity;
}

export function getRequiredPlanForFeature(feature: string): PlanKey {
  const normalizedFeature = DOTTED_TO_SHORT_FEATURE_MAP[feature] || feature;
  if (PLANS_CONFIG.free.features.includes(normalizedFeature) || PLANS_CONFIG.free.features.includes(feature)) {
    return 'free';
  }
  if (PLANS_CONFIG.bronze.features.includes(normalizedFeature) || PLANS_CONFIG.bronze.features.includes(feature)) {
    return 'bronze';
  }
  if (PLANS_CONFIG.silver.features.includes(normalizedFeature) || PLANS_CONFIG.silver.features.includes(feature)) {
    return 'silver';
  }
  return 'custom';
}

export function getRequiredPlanForActivity(activityType: string): PlanKey {
  const act = activityType.toLowerCase();
  const normalizedAct = act === 'idea_pitching' ? 'ideathon' : act === 'ideathon' ? 'idea_pitching' : act;
  if (PLANS_CONFIG.free.allowedActivityTypes.includes(act) || PLANS_CONFIG.free.allowedActivityTypes.includes(normalizedAct)) {
    return 'free';
  }
  if (PLANS_CONFIG.bronze.allowedActivityTypes.includes(act) || PLANS_CONFIG.bronze.allowedActivityTypes.includes(normalizedAct)) {
    return 'bronze';
  }
  if (PLANS_CONFIG.silver.allowedActivityTypes.includes(act) || PLANS_CONFIG.silver.allowedActivityTypes.includes(normalizedAct)) {
    return 'silver';
  }
  return 'custom';
}

export function getRequiredPlanForParticipants(count: number): PlanKey {
  if (count <= 100) return 'free';
  if (count <= 1000) return 'bronze';
  if (count <= 3000) return 'silver';
  return 'custom';
}

export function getRequiredPlanForPaidEvents(): PlanKey {
  return 'bronze';
}

export function formatCommissionRate(planKey: string, commissionOverride?: number | null): string {
  if (commissionOverride != null && Number.isFinite(commissionOverride)) {
    return `${Number(commissionOverride).toFixed(2)}%`;
  }
  const plan = getPlan(planKey);
  if (plan.key === 'custom') {
    return 'Custom Rate';
  }
  return `${plan.commissionRate.toFixed(2)}%`;
}

export function formatParticipantLimit(value?: number | null, planKey?: string): string {
  const key = (planKey || '').toLowerCase();
  if (key === 'custom' || value == null) {
    return 'Negotiated per event';
  }
  if (value <= 0) {
    return 'Unlimited per event';
  }
  return `${value.toLocaleString()} per event`;
}

export function getParticipantLimitErrorMessage(currentPlanKey: string, requestedCount: number): string | null {
  const plan = getPlan(currentPlanKey);
  if (plan.maxParticipantsPerActivity === null) return null;
  if (requestedCount > plan.maxParticipantsPerActivity) {
    const nextPlan = getPlan(getRequiredPlanForParticipants(requestedCount));
    return `Your ${plan.name} plan allows up to ${plan.maxParticipantsPerActivity.toLocaleString()} participants per event — upgrade to ${nextPlan.name} for higher limits`;
  }
  return null;
}

